import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Volume2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { CustomDialog } from '@/components/common';
import { useAudioPermission } from '@/hooks';

interface AudioGuardProps {
  children: React.ReactNode;
}

export const AudioGuard: React.FC<AudioGuardProps> = ({ children }) => {
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [isTestingAudio, setIsTestingAudio] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { canPlayAudio, checked, checkAudioPermission } = useAudioPermission();

  useEffect(() => {
    if (checked && !canPlayAudio) {
      setShowPermissionDialog(true);
    }
  }, [checked, canPlayAudio]);

  const handleRequestPermission = async () => {
    setIsTestingAudio(true);

    const canPlay = checkAudioPermission();
    if (canPlay) {
      setShowPermissionDialog(false);
    } else {
      setError('Audio permission was not granted');
    }
    setIsTestingAudio(false);
  };

  const handleSkipPermission = () => {
    setShowPermissionDialog(false);
  };

  return (
    <>
      <CustomDialog
        open={showPermissionDialog}
        onClose={handleSkipPermission}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-container': {
            '& .MuiDialog-paper': {
              borderRadius: 2,
              border: 2,
              margin: 1,
              borderColor: 'primary.main',
            },
          },
        }}
        PaperProps={{
          elevation: 0,
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Volume2Icon size={24} />
            <Typography variant="h6">Allow Audio Notifications</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            This will help you stay informed about new orders and updates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {error && (
            <Typography variant="body2" color="error" paragraph>
              {error}
            </Typography>
          )}
          <Button
            onClick={handleRequestPermission}
            variant="contained"
            sx={{
              width: '150px',
            }}
            disabled={isTestingAudio}
            startIcon={<Volume2Icon size={16} />}
          >
            {isTestingAudio ? 'Testing Audio...' : 'Allow'}
          </Button>
        </DialogActions>
      </CustomDialog>
      {children}
    </>
  );
};
